import * as React from "react"
import { Box, Flex, Input, Stack, Stat, StatLabel, StatNumber, Text } from "@chakra-ui/react"
import { matchSorter } from "match-sorter"
import { useQuery } from "react-query"

import { useOrgs } from "./providers/OrgProvider"
import { BikeState } from "../lib/types/bike"
import { BikesMapZoneCard } from "./BikesMapZoneCard"
import { useGetDropzonesQuery } from "../lib/hooks/queries/useGetMapZones"
import { ZoneEntity } from "../lib/types/zone"

interface Props {
  bikeStates: BikeState[]
  bikeModels: string[]
  setSelectedZone: React.Dispatch<React.SetStateAction<ZoneEntity | undefined>>
}

export function BikesMapSidebar({ bikeStates, bikeModels, setSelectedZone }: Readonly<Props>) {
  const { currentOrgs } = useOrgs()

  const [search, setSearch] = React.useState("")

  const bikeModelQuery = bikeModels.reduce((acc, s) => acc + `&model=${s}`, "")

  const bikeStateQuery = bikeStates.reduce((acc, s) => acc + `&state=${s}`, "")

  const currentOrgsUuids = currentOrgs.map(({ uuid }) => uuid)

  // we only need the count here so let's ask for minimum amount of results (1)
  const { data } = useQuery<{ count: number }, string>(
    `bicycles/?organizations=${currentOrgsUuids.join()}${bikeStateQuery}${bikeModelQuery}&page_size=1`,
    { staleTime: Infinity },
  )

  const bikeCount = data?.count

  const { dropzones } = useGetDropzonesQuery({ enabled: true })
  const zones = dropzones ?? []

  const matchedZones = matchSorter(zones, search, { keys: ["name"] })

  return (
    <Flex minH="100%" maxH="100%" p={6} pt={2} direction="column" justify="flex-start">
      <Stat flex={0}>
        <StatLabel>Total assets</StatLabel>
        <StatNumber h="36px">{bikeCount}</StatNumber>
      </Stat>
      <Flex align="center" justify="space-between" my={4}>
        <Box>
          <Text fontSize="1.2rem" fontWeight="600">
            Dropzones
          </Text>
          <Text fontSize="0.7rem" color="gray.600">
            Available assets per zone
          </Text>
        </Box>
        <Input
          placeholder="Search..."
          w={160}
          size="sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Flex>
      <Stack overflowY="auto" flexGrow={1}>
        {matchedZones.map((zone, index) => (
          <BikesMapZoneCard
            key={zone.uuid}
            zone={zone}
            setSelectedZone={setSelectedZone}
            bgColor={index % 2 === 0 ? "gray.50" : "white"}
          />
        ))}
      </Stack>
    </Flex>
  )
}
